import { createRef, Component } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { models, service, Embed, IEmbedConfiguration } from 'powerbi-client';
import { ModalWrapper } from '@mews-commander/ui';

export interface OwnProps {
    id?: string;
    embedUrl?: string;
    onEmbedded?: (report: Embed) => void;
    accessToken?: string;
    language?: string;
    formatLocale?: string;
    navContentPaneEnabled?: boolean;
    filterPaneEnabled?: boolean;
    platformScopeProfileId?: string;
}

type Props = OwnProps & {
    newContentPaneEnabled?: boolean;
    filterPaneEnabled?: boolean;
};

declare global {
    const powerbi: service.Service;
}

export class Report extends Component<Props> {
    static defaultProps = {
        navContentPaneEnabled: false,
        filterPaneEnabled: true,
    };

    component: Embed | null = null;

    rootElementRef = createRef<HTMLDivElement>();

    componentDidUpdate() {
        this.refreshReport();
    }

    componentDidMount() {
        this.refreshReport();
    }

    componentWillUnmount() {
        this.reset();
    }

    refreshReport() {
        const {
            id,
            embedUrl,
            accessToken,
            navContentPaneEnabled,
            language,
            formatLocale,
            filterPaneEnabled,
            platformScopeProfileId,
        } = this.props;
        const embedConfig: IEmbedConfiguration = {
            ...DEFAULT_EMBED_CONFIG,
            id,
            embedUrl,
            accessToken,
            slicers: platformScopeProfileId ? [slicers] : undefined,
            settings: {
                navContentPaneEnabled,
                filterPaneEnabled,
                background: models.BackgroundType.Transparent,
                localeSettings: {
                    language,
                    formatLocale,
                },
            },
        };

        if (this.hasError(embedConfig)) {
            if (!this.component) {
                this.embed(embedConfig);
            }

            // @ts-expect-error $TSFixMeError: Object is possibly 'null'.
            void this.component?.setAccessToken(embedConfig.accessToken);
        }
    }

    getRootElement() {
        // eslint-disable-next-line react/no-find-dom-node
        return ReactDOM.findDOMNode(this.rootElementRef.current) as HTMLDivElement;
    }

    embed(config: IEmbedConfiguration) {
        this.component = powerbi.embed(this.getRootElement(), config);
        if (this.props.onEmbedded) {
            this.props.onEmbedded(this.component);
        }
    }

    reset() {
        powerbi.reset(this.getRootElement());
        this.component = null;
    }

    hasError = (config: IEmbedConfiguration) => {
        const errors = models.validateReportLoad(config);
        return !errors;
    };

    render() {
        return <ReportWrapper ref={this.rootElementRef} />;
    }
}

const DEFAULT_EMBED_CONFIG = {
    type: 'report',
    permissions: models.Permissions.Read,
    viewMode: models.ViewMode.View,
    tokenType: models.TokenType.Embed,
};

const targets = {
    table: 'enterprise',
    column: 'Name',
};

const basicFilter: models.ISlicerFilter = {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: targets,
    operator: 'In',
    values: ['_blank'],
    filterType: models.FilterType.Basic,
};

const slicers: models.ISlicer = {
    selector: {
        $schema: 'http://powerbi.com/product/schema#slicerTargetSelector',
        target: targets,
    },
    state: {
        filters: [basicFilter],
    },
};

const ReportWrapper = styled(ModalWrapper).attrs({ className: 'powerbi-frame' })`
    & > iframe {
        border: 0; /* removes the border around iframe */
    }
`;
